import { useCallback, useEffect, useState } from "react";
import { FileText, Mail, MessageSquare, Phone, Search, Upload } from "lucide-react";
import { toast } from "sonner";

import { API, api, apiError, fmtDate, relativeTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, PageHeader, StatusBadge, TableSkeleton } from "@/components/common/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SelectField } from "@/components/forms/Fields";

export function Communications() {
  const [data, setData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [channel, setChannel] = useState("all");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setData(null);
    try {
      const { data: res } = await api.get("/communications", {
        params: { channel: channel !== "all" ? channel : undefined, search: search || undefined },
      });
      setData(res);
    } catch (e) {
      toast.error(apiError(e));
      setData({ items: [] });
    }
  }, [channel, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    api.get("/communications/templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, []);

  const icon = (c) => (c === "Email" ? Mail : c === "Call" ? Phone : c === "Note" ? FileText : MessageSquare);

  return (
    <div className="space-y-6">
      <PageHeader
        meta="Engagement"
        title="Communications"
        subtitle="Every email, call and note logged against candidates and clients."
      />

      <Tabs defaultValue="timeline">
        <TabsList className="border border-slate-200 bg-white p-1">
          <TabsTrigger value="timeline" data-testid="comms-tab-timeline" className="rounded px-4 py-1.5 text-xs data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700">
            Timeline
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="comms-tab-templates" className="rounded px-4 py-1.5 text-xs data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700">
            Templates
          </TabsTrigger>
        </TabsList>

        <TabsContent value="timeline" className="mt-5 space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative max-w-sm flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="comms-search-input"
                placeholder="Search subject or message"
                className="h-9 border-slate-200 bg-slate-50 pl-9 text-sm text-slate-800 placeholder:text-slate-400" />
            </div>
            <SelectField value={channel} onChange={setChannel} allowAll className="w-48" testid="comms-channel-filter"
              options={["Email", "SMS", "WhatsApp", "Call", "Note"]} placeholder="Channel" />
          </div>

          {!data ? (
            <TableSkeleton rows={6} cols={3} />
          ) : data.items.length === 0 ? (
            <EmptyState icon={MessageSquare} title="No communication logged" description="Emails sent from candidate profiles appear here." testid="comms-empty-state" />
          ) : (
            <ul className="surface divide-y divide-slate-200 rounded-md">
              {data.items.map((m) => {
                const Icon = icon(m.channel);
                return (
                  <li key={m.id} className="flex gap-4 p-4" data-testid="comm-row">
                    <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded border border-slate-200 bg-slate-100">
                      <Icon className="h-3.5 w-3.5 text-blue-600" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm text-slate-900">{m.subject || m.channel}</p>
                        <div className="flex items-center gap-2">
                          <StatusBadge value={m.channel} />
                          {m.delivery === "sent" && <StatusBadge value="Completed" />}
                        </div>
                      </div>
                      <p className="mt-1.5 line-clamp-3 whitespace-pre-line text-xs leading-relaxed text-slate-500">{m.body}</p>
                      <p className="mt-1.5 text-[10px] text-slate-400">
                        {m.candidate_name || m.client_name || "Internal"} · {m.actor_name} · {relativeTime(m.created_at)}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </TabsContent>

        <TabsContent value="templates" className="mt-5">
          <div className="grid gap-4 lg:grid-cols-2">
            {templates.map((t) => (
              <article key={t.key} className="surface rounded-md p-5" data-testid="template-card">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-medium text-slate-900">{t.name}</p>
                  <StatusBadge value={t.category} />
                </div>
                <p className="mt-2 text-xs text-slate-500">Subject: {t.subject}</p>
                <p className="mt-3 whitespace-pre-line rounded border border-slate-200 bg-slate-50 p-3 text-[11px] leading-relaxed text-slate-500">
                  {t.body}
                </p>
              </article>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export function Documents() {
  const { meta, isInternal } = useAuth();
  const [data, setData] = useState(null);
  const [docType, setDocType] = useState("all");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setData(null);
    try {
      const { data: res } = await api.get("/documents", {
        params: { doc_type: docType !== "all" ? docType : undefined, search: search || undefined },
      });
      setData(res);
    } catch (e) {
      toast.error(apiError(e));
      setData({ items: [] });
    }
  }, [docType, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="space-y-6">
      <PageHeader
        meta="Secure storage"
        title="Documents"
        subtitle="Resumes, IDs, offer letters and client contracts with versioning and access control."
      />

      <div className="flex flex-col gap-3 sm:flex-row">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="documents-search-input"
          placeholder="Search by file name"
          className="h-9 max-w-sm border-slate-200 bg-slate-50 text-sm text-slate-800 placeholder:text-slate-400" />
        <SelectField value={docType} onChange={setDocType} allowAll className="w-56" testid="documents-type-filter"
          options={meta?.enums?.doc_types || []} placeholder="Document type" />
      </div>

      {!data ? (
        <TableSkeleton rows={6} cols={4} />
      ) : data.items.length === 0 ? (
        <EmptyState icon={Upload} title="No documents yet" description="Upload documents from a candidate or client profile." testid="documents-empty-state" />
      ) : (
        <div className="surface divide-y divide-slate-200 rounded-md">
          {data.items.map((d) => (
            <div key={d.id} className="flex flex-wrap items-center justify-between gap-4 p-4" data-testid="document-list-row">
              <div className="min-w-0">
                <p className="truncate text-sm text-slate-900">{d.name}</p>
                <p className="mt-0.5 text-[11px] text-slate-500">
                  {d.owner_name || "Unlinked"} · {d.doc_type} · v{d.version} · {fmtDate(d.created_at)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge value={d.visibility === "client" ? "Client visible" : "Internal"} />
                {d.storage_path ? (
                  <Button size="sm" variant="outline" onClick={() => window.open(`${API}/documents/${d.id}/download`, "_blank")}
                    data-testid="document-preview-button" className="h-8 rounded-full border-slate-200 bg-transparent text-xs text-slate-600">
                    Preview
                  </Button>
                ) : (
                  <span className="text-[11px] text-slate-400">Demo record</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function Offers() {
  const { meta } = useAuth();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("all");
  const [refresh, setRefresh] = useState(0);

  const load = useCallback(async () => {
    setData(null);
    try {
      const { data: res } = await api.get("/offers", {
        params: { status: status !== "all" ? status : undefined },
      });
      setData(res);
    } catch (e) {
      toast.error(apiError(e));
      setData({ items: [] });
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load, refresh]);

  const setOfferStatus = async (id, value) => {
    try {
      await api.patch(`/offers/${id}/status`, { status: value });
      toast.success(`Offer marked ${value}`);
      setRefresh((r) => r + 1);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setJoining = async (id, value) => {
    try {
      const payload = { status: value };
      if (value === "Dropout" || value === "No Show") {
        payload.dropout_reason = window.prompt("Reason for dropout / no show?") || "Not recorded";
      }
      if (value === "Joined") payload.actual_joining_date = new Date().toISOString().slice(0, 10);
      await api.patch(`/offers/${id}/joining`, payload);
      toast.success(`Joining status set to ${value}`);
      setRefresh((r) => r + 1);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        meta="Closure"
        title="Offers &amp; Joining"
        subtitle="Track offers from draft through acceptance, joining confirmation and dropouts."
      />

      <SelectField value={status} onChange={setStatus} allowAll className="w-56" testid="offers-status-filter"
        options={meta?.enums?.offer_statuses || []} placeholder="Offer status" />

      {!data ? (
        <TableSkeleton rows={5} cols={5} />
      ) : data.items.length === 0 ? (
        <EmptyState icon={FileText} title="No offers yet" description="Raise an offer from a candidate profile once they are selected." testid="offers-empty-state" />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {data.items.map((o) => (
            <article key={o.id} className="surface rounded-md p-5" data-testid="offer-card">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-[15px] text-slate-900">{o.candidate_name}</p>
                  <p className="truncate text-xs text-slate-500">{o.job_title} · {o.client_name || "Client not available"}</p>
                </div>
                <StatusBadge value={o.status} />
              </div>

              <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
                {[["Offered", `${o.offered_ctc} LPA`], ["Expected", o.expected_ctc || "—"],
                  ["Joining bonus", o.joining_bonus ? `₹${o.joining_bonus.toLocaleString("en-IN")}` : "—"],
                  ["Joining date", fmtDate(o.joining_date)]].map(([k, v]) => (
                  <div key={k}>
                    <p className="label-xs">{k}</p>
                    <p className="font-mono-num mt-1 text-xs text-slate-800">{v}</p>
                  </div>
                ))}
              </div>

              {o.joining_status && (
                <div className="mt-4 flex items-center gap-2">
                  <StatusBadge value={o.joining_status} />
                  {o.dropout_reason && <span className="text-[11px] text-rose-700/90">{o.dropout_reason}</span>}
                </div>
              )}

              <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-200 pt-4">
                {["Offer Sent", "Offer Accepted", "Offer Declined"]
                  .filter((s) => s !== o.status)
                  .map((s) => (
                    <Button key={s} size="sm" variant="ghost" onClick={() => setOfferStatus(o.id, s)} data-testid="offer-status-button"
                      className="h-8 rounded-full border border-slate-200 text-[11px] text-slate-600 hover:bg-slate-100">
                      {s}
                    </Button>
                  ))}
                {["Joining Confirmed", "Joined", "Dropout", "No Show"].map((s) => (
                  <Button key={s} size="sm" variant="ghost" onClick={() => setJoining(o.id, s)} data-testid="joining-status-button"
                    className="h-8 rounded-full border border-slate-200 text-[11px] text-slate-500 hover:bg-slate-100">
                    {s}
                  </Button>
                ))}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
